import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Grid,
  Text,
  useAccordionItemState,
  useBreakpointValue,
  useColorModeValue,
  VStack,
  type ChakraProps,
} from '@chakra-ui/react';
import { countriesInfo } from '@/models/country';
import type { Country } from '@/models/country';
import type { LiveMatchEvent } from '@/models/liveMatch';
import { formatPenaltyScoreLine, type PenaltyScore } from '@/helpers/fixtures/matchScoreDisplay';
import { Flag } from '../Flag';
import { FIXTURE_MATCH_CHEVRON_COLUMN, FIXTURE_MATCH_GRID_COLUMNS_WITH_CHEVRON } from '../FixtureMatchRow';
import { TeamDetailTrigger } from '../teamDetail/TeamDetailTrigger';
import { isClickableTeamCountry } from '../teamDetail/teamDetailUtils';
import { filterDisplayableEvents } from './matchEventDisplay';
import { MatchEventRow } from './MatchEventRow';
import { MatchPointsRow } from './MatchPointsRow';

function MatchTeamCell(props: {
  country: Country;
  fontSize: ChakraProps['fontSize'];
  nameLines: number;
  svg?: boolean;
  nameCol: number;
  flagCol: number;
}) {
  const clickable = isClickableTeamCountry(props.country);
  const flag = (
    <Flag country={props.country} w={6} borderRadius='3px' flexShrink={0} svg={props.svg} justifySelf='start' />
  );
  const nameEl = (
    <Text
      fontSize={props.fontSize}
      fontWeight='semibold'
      lineHeight='short'
      noOfLines={props.nameLines}
      textAlign='left'
      minW={0}
    >
      {countriesInfo[props.country].name}
    </Text>
  );

  return (
    <>
      <Box gridColumn={props.flagCol} gridRow={1} display='flex' alignItems='center'>
        {clickable ? (
          <TeamDetailTrigger country={props.country} source='fixtures' variant='inline' justifySelf='start'>
            {flag}
          </TeamDetailTrigger>
        ) : (
          flag
        )}
      </Box>
      <Box gridColumn={props.nameCol} gridRow={1} display='flex' alignItems='center' minW={0}>
        {clickable ? (
          <TeamDetailTrigger country={props.country} source='fixtures' variant='compact'>
            {nameEl}
          </TeamDetailTrigger>
        ) : (
          nameEl
        )}
      </Box>
    </>
  );
}

type MatchScoreHeaderProps = {
  homeTeam: Country;
  awayTeam: Country;
  goalsScoreLine: string;
  penaltyScore?: PenaltyScore;
  metaLine: string;
  variant: 'live' | 'result';
  statusBadge?: string;
  svg?: boolean;
  expandable: boolean;
  isOpen: boolean;
  fontSize: ChakraProps['fontSize'];
  metaFontSize: ChakraProps['fontSize'];
  nameLines: number;
  gridColumns: string;
  columnGap: number;
  scoreFontSize: ChakraProps['fontSize'];
  scoreColor: string;
  metaColor: string;
  badgeColor: string;
  rowBg: string;
};

function MatchScoreHeaderGrid(props: MatchScoreHeaderProps) {
  const isLive = props.variant === 'live';
  const isOpen = props.isOpen;
  const showPenaltyRow = isOpen && props.penaltyScore != null;
  const showLiveBadge = isLive && props.statusBadge && !showPenaltyRow;
  const metaRow = 2 + (showPenaltyRow ? 1 : 0);

  return (
    <Grid
      templateColumns={props.gridColumns}
      columnGap={props.columnGap}
      rowGap={2}
      alignItems='center'
      w='full'
      py={2}
      px={{ base: 2, sm: 3 }}
    >
      <MatchTeamCell
        country={props.homeTeam}
        fontSize={props.fontSize}
        nameLines={props.nameLines}
        svg={props.svg}
        flagCol={1}
        nameCol={2}
      />
      <Text
        gridColumn={3}
        gridRow={1}
        fontSize={props.scoreFontSize}
        fontWeight='bold'
        color={props.scoreColor}
        textAlign='center'
        whiteSpace='nowrap'
        minW={0}
        px={0.5}
      >
        {props.goalsScoreLine}
      </Text>
      <MatchTeamCell
        country={props.awayTeam}
        fontSize={props.fontSize}
        nameLines={props.nameLines}
        svg={props.svg}
        flagCol={4}
        nameCol={5}
      />
      {props.expandable ? (
        <Box
          gridColumn={6}
          gridRow={1}
          display='flex'
          alignItems='center'
          justifyContent='center'
          w={FIXTURE_MATCH_CHEVRON_COLUMN}
          minW={FIXTURE_MATCH_CHEVRON_COLUMN}
        >
          <AccordionIcon flexShrink={0} />
        </Box>
      ) : (
        <Box gridColumn={6} gridRow={1} aria-hidden w={FIXTURE_MATCH_CHEVRON_COLUMN} minW={FIXTURE_MATCH_CHEVRON_COLUMN} />
      )}
      {showPenaltyRow ? (
        <Text
          gridColumn={3}
          gridRow={2}
          fontSize={props.metaFontSize}
          fontWeight='semibold'
          color={props.scoreColor}
          textAlign='center'
          whiteSpace='nowrap'
          lineHeight='short'
        >
          {formatPenaltyScoreLine(props.penaltyScore!.home, props.penaltyScore!.away)}
        </Text>
      ) : null}
      {showLiveBadge ? (
        <Text
          gridColumn={3}
          gridRow={2}
          fontSize={props.metaFontSize}
          fontWeight='bold'
          color={props.badgeColor}
          textTransform='uppercase'
          textAlign='center'
          whiteSpace='nowrap'
          lineHeight='short'
          bg={props.rowBg}
          px={1}
        >
          {props.statusBadge}
        </Text>
      ) : null}
      {props.metaLine ? (
        <Text
          gridColumn='1 / -1'
          gridRow={metaRow}
          fontSize={props.metaFontSize}
          color={props.metaColor}
          lineHeight='short'
          textAlign='left'
        >
          {props.metaLine}
        </Text>
      ) : null}
    </Grid>
  );
}

function MatchScoreHeaderInAccordion(props: Omit<MatchScoreHeaderProps, 'isOpen'>) {
  const { isOpen } = useAccordionItemState();
  return <MatchScoreHeaderGrid {...props} isOpen={isOpen} />;
}

export function MatchScoreAccordionRow(props: {
  homeTeam: Country;
  awayTeam: Country;
  goalsScoreLine: string;
  penaltyScore?: PenaltyScore;
  metaLine: string;
  events: LiveMatchEvent[];
  variant: 'live' | 'result';
  statusBadge?: string;
  bracketLabel?: string;
  showBracketLabel?: boolean;
  svg?: boolean;
  expandable?: boolean;
  homePoints?: number;
  awayPoints?: number;
  fontSize?: number;
  metaFontSize?: number;
  nameLines?: number;
}) {
  const gridColumns =
    useBreakpointValue(FIXTURE_MATCH_GRID_COLUMNS_WITH_CHEVRON) ?? FIXTURE_MATCH_GRID_COLUMNS_WITH_CHEVRON.base;
  const responsiveFontSize = useBreakpointValue({ base: 13, sm: 15 });
  const responsiveMetaFontSize = useBreakpointValue({ base: 10, sm: 12 });
  const responsiveNameLines = useBreakpointValue({ base: 1, sm: 2 }) ?? 1;
  const fontSize = props.fontSize ?? responsiveFontSize;
  const metaFontSize = props.metaFontSize ?? responsiveMetaFontSize;
  const nameLines = props.nameLines ?? responsiveNameLines;
  const columnGap = useBreakpointValue({ base: 2, sm: 3 }) ?? 2;
  const scoreFontSize = useBreakpointValue({ base: 'xs', sm: 'sm' });
  const scoreColor = useColorModeValue('gray.800', 'gray.100');
  const metaColor = useColorModeValue('gray.600', 'gray.400');
  const matchLabelColor = useColorModeValue('gray.700', 'gray.300');

  const isLive = props.variant === 'live';
  const rowBg = useColorModeValue(isLive ? 'green.50' : 'white', isLive ? 'green.900' : 'gray.800');
  const borderColor = useColorModeValue(
    isLive ? 'green.200' : 'blackAlpha.200',
    isLive ? 'green.700' : 'whiteAlpha.300',
  );
  const pointsDividerColor = useColorModeValue('blackAlpha.100', 'whiteAlpha.150');
  const badgeColor = useColorModeValue('green.700', 'green.200');

  const displayEvents = filterDisplayableEvents(props.events);
  const expandable = props.expandable ?? true;
  const showPoints = props.homePoints != null && props.awayPoints != null;
  const pointsRow = showPoints ? (
    <MatchPointsRow
      homeTeam={props.homeTeam}
      awayTeam={props.awayTeam}
      homePoints={props.homePoints!}
      awayPoints={props.awayPoints!}
      gridColumns={gridColumns}
      columnGap={columnGap}
      svg={props.svg}
      includeChevronSpacer
    />
  ) : null;

  const headerProps: Omit<MatchScoreHeaderProps, 'isOpen'> = {
    homeTeam: props.homeTeam,
    awayTeam: props.awayTeam,
    goalsScoreLine: props.goalsScoreLine,
    penaltyScore: props.penaltyScore,
    metaLine: props.metaLine,
    variant: props.variant,
    statusBadge: props.statusBadge,
    svg: props.svg,
    expandable,
    fontSize: fontSize ?? 13,
    metaFontSize,
    nameLines,
    gridColumns,
    columnGap,
    scoreFontSize,
    scoreColor,
    metaColor,
    badgeColor,
    rowBg,
  };

  return (
    <Box w='full' minW={0}>
      {props.showBracketLabel && props.bracketLabel ? (
        <Text fontSize={metaFontSize} fontWeight='semibold' color={matchLabelColor} pb={2}>
          {props.bracketLabel}
        </Text>
      ) : null}
      <Box
        w='full'
        borderRadius='md'
        borderWidth='1px'
        borderColor={borderColor}
        bg={rowBg}
        overflow='hidden'
      >
        {expandable ? (
          <Accordion allowToggle w='full'>
            <AccordionItem border='none'>
              <AccordionButton p={0} _hover={{ bg: 'transparent' }} _expanded={{ bg: 'transparent' }} w='full'>
                <MatchScoreHeaderInAccordion {...headerProps} />
              </AccordionButton>
              <AccordionPanel
                px={{ base: 2, sm: 3 }}
                pb={3}
                pt={2}
                borderTopWidth='1px'
                borderTopColor={borderColor}
              >
                {showPoints || displayEvents.length > 0 ? (
                  <VStack align='stretch' spacing={2}>
                    {displayEvents.map((event, i) => (
                      <MatchEventRow
                        key={`${event.minute}-${event.player}-${i}`}
                        event={event}
                        homeTeam={props.homeTeam}
                        awayTeam={props.awayTeam}
                      />
                    ))}
                    {showPoints ? (
                      <Box
                        pt={displayEvents.length > 0 ? 2 : 0}
                        borderTopWidth={displayEvents.length > 0 ? '1px' : 0}
                        borderTopColor={pointsDividerColor}
                      >
                        {pointsRow}
                      </Box>
                    ) : null}
                  </VStack>
                ) : null}
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        ) : (
          <Box w='full'>
            <MatchScoreHeaderGrid {...headerProps} isOpen={props.penaltyScore != null} />
            {!expandable && showPoints ? (
              <Box px={{ base: 2, sm: 3 }} pb={1}>
                {pointsRow}
              </Box>
            ) : null}
          </Box>
        )}
      </Box>
    </Box>
  );
}
